import React, { useState, useEffect } from 'react';
import './InviteModal.css';

type OrgRole = {
  id: string;
  name: string;
};

type ProfileTemplate = {
  id: string;
  name: string;
};

type InviteRow = {
  email: string;
  roleId: string;
  /** The profile the invitee is assigned on acceptance. Never blank. */
  profileTemplateId: string;
};

type InviteModalProps = {
  isOpen: boolean;
  onClose: () => void;
  orgRoles: OrgRole[];
  /** The org's profile templates, for the Profile dropdown. */
  profileTemplates: ProfileTemplate[];
  onInvitesSent: () => void;
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function InviteModal({
  isOpen,
  onClose,
  orgRoles,
  profileTemplates,
  onInvitesSent,
}: InviteModalProps) {
  const selectableRoles = orgRoles.filter(r => r.name !== 'Owner');
  const defaultRoleId = selectableRoles[0]?.id ?? '';

  // Alphabetical, like every other template list in the app.
  const sortedTemplates = [...profileTemplates].sort((a, b) => a.name.localeCompare(b.name));

  // There is deliberately no blank option. A user with no profile assignment
  // cannot pass the Profiles gate, so an invite sent without one delivers
  // somebody to a locked door that only an administrator can open — which is
  // the manual step this dropdown exists to remove. The first template stands
  // in until the admin chooses; Send refuses when there is none to stand in.
  const defaultTemplateId = sortedTemplates[0]?.id ?? '';

  const newRow = (): InviteRow => ({
    email: '',
    roleId: defaultRoleId,
    profileTemplateId: defaultTemplateId,
  });

  const [rows, setRows]       = useState<InviteRow[]>([newRow()]);
  const [sending, setSending] = useState(false);
  const [error, setError]     = useState('');

  // Reset form each time the modal opens
  useEffect(() => {
    if (isOpen) {
      setRows([{ email: '', roleId: defaultRoleId, profileTemplateId: defaultTemplateId }]);
      setError('');
    }
  }, [isOpen, defaultRoleId, defaultTemplateId]);

  if (!isOpen) return null;

  const updateRow = (index: number, field: keyof InviteRow, value: string) => {
    setRows(prev => prev.map((r, i) => i === index ? { ...r, [field]: value } : r));
    setError('');
  };

  const addRow = () => {
    setRows(prev => [...prev, newRow()]);
  };

  const removeRow = (index: number) => {
    setRows(prev => prev.filter((_, i) => i !== index));
  };

  const validate = (): string | null => {
    // Nothing to assign, so nothing can be sent. Worth its own message: an
    // admin staring at an empty Profile dropdown needs to be told to go and
    // build a template, not left guessing why Send does nothing.
    if (sortedTemplates.length === 0) {
      return 'No profile templates exist yet. Create one under Profiles before inviting users.';
    }

    const filledRows = rows.filter(r => r.email.trim() !== '');
    if (filledRows.length === 0) return 'Enter at least one email address.';

    for (const row of filledRows) {
      if (!EMAIL_REGEX.test(row.email.trim())) return `"${row.email.trim()}" is not a valid email address.`;
      if (!row.roleId) return 'Each invite must have a role selected.';
      if (!row.profileTemplateId) return 'Each invite must have a profile selected.';
    }

    const emails = filledRows.map(r => r.email.trim().toLowerCase());
    if (new Set(emails).size !== emails.length) return 'Duplicate email addresses found.';

    return null;
  };

  const handleSend = async () => {
    const validationError = validate();
    if (validationError) { setError(validationError); return; }

    setSending(true);
    try {
      const filledRows = rows.filter(r => r.email.trim() !== '');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/invites`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          invites: filledRows.map(r => ({
            email: r.email.trim(),
            roleId: r.roleId,
            profileTemplateId: r.profileTemplateId,
          })),
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Failed to send invites.');

      const failed: { email: string; reason: string }[] = data.failed ?? [];
      if (failed.length > 0 && (data.sent ?? []).length === 0) {
        setError(failed.map(f => `${f.email}: ${f.reason}`).join('\n'));
        return;
      }
      if (failed.length > 0) {
        setError(`Some invites could not be sent:\n${failed.map(f => `${f.email}: ${f.reason}`).join('\n')}`);
      }

      onInvitesSent();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to send invites. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="smrtdocs-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="smrtdocs-modal invite-modal">
        <div className="smrtdocs-modal-header">
          <h2 style={{ margin: 0, fontSize: '1rem' }}>Invite Users</h2>
        </div>

        <div className="smrtdocs-modal-body">
          {/* Three fields side by side stop reading as a sentence, so they get
              headings. Kept on the same grid as the rows below. */}
          <div className="invite-row invite-row-header">
            <span className="invite-email-input">Email address</span>
            <span className="invite-role-select">Role</span>
            <span className="invite-profile-select">Profile</span>
            {rows.length > 1 && <span className="invite-remove-spacer" />}
          </div>

          <div className="invite-rows">
            {rows.map((row, i) => (
              <div key={i} className="invite-row">
                <input
                  type="email"
                  className="invite-email-input"
                  placeholder="Email address"
                  value={row.email}
                  onChange={(e) => updateRow(i, 'email', e.target.value)}
                  disabled={sending}
                />
                <select
                  className="invite-role-select"
                  value={row.roleId}
                  onChange={(e) => updateRow(i, 'roleId', e.target.value)}
                  disabled={sending}
                >
                  {selectableRoles.map(role => (
                    <option key={role.id} value={role.id}>{role.name}</option>
                  ))}
                </select>
                {/* What they must complete before the Profiles gate opens.
                    Required — see defaultTemplateId above. */}
                <select
                  className="invite-profile-select"
                  value={row.profileTemplateId}
                  onChange={(e) => updateRow(i, 'profileTemplateId', e.target.value)}
                  disabled={sending || sortedTemplates.length === 0}
                >
                  {sortedTemplates.length === 0 ? (
                    <option value="">No templates</option>
                  ) : (
                    sortedTemplates.map(tpl => (
                      <option key={tpl.id} value={tpl.id}>{tpl.name}</option>
                    ))
                  )}
                </select>
                {rows.length > 1 && (
                  <button
                    className="invite-remove-btn"
                    onClick={() => removeRow(i)}
                    disabled={sending}
                    title="Remove"
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
          </div>

          <button className="invite-add-row-btn" onClick={addRow} disabled={sending}>
            + Add another
          </button>

          {error && <p className="invite-error">{error}</p>}
        </div>

        <div className="smrtdocs-modal-footer">
          <button className="btn-cancel" onClick={onClose} disabled={sending}>
            Cancel
          </button>
          <button className="btn-save" onClick={handleSend} disabled={sending}>
            {sending ? 'Sending…' : 'Send Invites'}
          </button>
        </div>
      </div>
    </div>
  );
}
